<template>
  <div class="q-pb-xl">
    <div class="q-pb-xl">
      <div class="fill-height q-px-xl q-pt-xl">
        <!-- ---------------- Clients ---------------- -->

        <!-- Title - Search - Add -->
        <div class="row q-mt-lg items-center justify-between q-mb-lg">
          <div class="col-shrink text-left">
            <span class="text-h5">{{ i18n.title }} ({{ workspaces.meta.count }})</span>
          </div>
          <div class="col-shrink row items-center">
            <!-- Add new client -->
            <Link :href="workspaces_new_link">
              <q-btn
                unelevated
                color="primary"
                :label="i18n.add_workspace"
                icon="las la-plus"
                no-caps
              />
            </Link>
          </div>
        </div>

        <!-- Search -->
        <div class="row q-mt-lg items-center justify-start q-mb-md">
          <!-- Search client -->
          <q-input
            v-model="filter_search"
            debounce="500"
            class="q-mr-md"
            outlined
            color="drawer-background"
            standout
            hide-bottom-space
            dense
            style="max-width: 240px"
            :placeholder="i18n.search"
          >
            <template v-slot:prepend>
              <q-icon name="search" />
            </template>
            <template v-slot:append>
              <q-icon
                v-if="filter_search && filter_search != ''"
                name="close"
                class="cursor-pointer"
                @click="filter_search = ''"
              />
            </template>
          </q-input>

          <!-- Account Manager Filter -->
          <q-select
            id="account-manager-filter"
            v-model="filter_account_manager"
            outlined
            dense
            options-dense
            hide-bottom-space
            :options="account_managers"
            :prefix="`${i18n.filter_account_manager_prefix}${filter_account_manager ? ':' : ''}`"
            color="primary"
            style="max-width: fit-content; width: 100%"
            class="select-filter"
            :class="{
              'select-filter-active': filter_account_manager,
            }"
            input-class="text-capitalize"
            popup-content-class="q-py-sm"
            emit-value
            map-options
            clearable
          >
            <template v-slot:option="scope">
              <q-item
                v-bind="scope.itemProps"
                dense
              >
                <q-item-section avatar>
                  <q-avatar size="md">
                    <img
                      :src="scope.opt.avatar_url"
                      referrerpolicy="no-referrer"
                    >
                  </q-avatar>
                </q-item-section>
                <q-item-section>
                  <q-item-label>{{ scope.opt.name }}</q-item-label>
                  <q-item-label caption>
                    {{ scope.opt.email }}
                  </q-item-label>
                </q-item-section>
              </q-item>
            </template>

            <template v-slot:selected>
              <template v-if="filter_account_manager">
                <q-avatar
                  size="16px"
                  class="q-ml-sm q-mr-xs"
                >
                  <img
                    :src="filter_account_manager.avatar_url"
                    referrerpolicy="no-referrer"
                  >
                </q-avatar>
                {{ filter_account_manager.name }}
              </template>
            </template>
          </q-select>

          <VOnboardingWrapper
            v-if="vonboarding"
            ref="vo_wrapper"
            :steps="vonboarding.steps"
            :options="vonboarding.options"
          >
            <template #default="{ previous, next, step, isFirst, isLast, index }">
              <VOnboardingStep>
                <q-card
                  class="q-pa-md onboarding-tooltip q-mt-md text-left"
                  style="max-width: 420px;"
                >
                  <div class="onboarding-tooltip-arrow" />
                  <q-card-section class="q-pa-none">
                    <div v-if="step.content">
                      <h3
                        v-if="step.content.title"
                        class="text-h6"
                      >
                        {{ step.content.title }}
                      </h3>
                      <div
                        v-if="step.content.description"
                        class="q-mt-sm"
                      >
                        <p class="text-caption">
                          {{ step.content.description }}
                        </p>
                      </div>
                    </div>
                  </q-card-section>
                  <q-card-actions align="right">
                    <span class="absolute right-0 bottom-full q-mb-sm q-mr-sm text-caption">{{ `${index + 1}/${vonboarding.steps.length}` }}</span>
                    <template v-if="!isFirst">
                      <q-btn
                        dense
                        outline
                        icon="chevron_left"
                        @click="previous"
                      />
                    </template>
                    <template v-if="isLast">
                      <q-btn
                        dense
                        :label="$q.lang.label.ok"
                        color="primary"
                        unelevated
                        @click="vonboarding_finish"
                      />
                    </template>
                    <template v-else>
                      <q-btn
                        dense
                        color="primary"
                        unelevated
                        icon-right="chevron_right"
                        @click="next"
                      />
                    </template>
                  </q-card-actions>
                </q-card>
              </VOnboardingStep>
            </template>
          </VOnboardingWrapper>
        </div>

        <!-- Clients list -->
        <q-markup-table
          v-if="workspaces?.meta?.count != 0"
          class="full-width q-pa-none q-mt-md app-table"
          flat
          separator="none"
        >
          <thead>
            <tr>
              <th class="text-left">
                {{ i18n.table_col_1 }}
              </th>
              <th class="text-left">
                {{ i18n.table_col_2 }}
              </th>
              <th class="text-left">
                {{ i18n.table_col_3 }}
              </th>
              <th class="text-left">
                {{ i18n.table_col_4 }}
              </th>
              <th class="text-left">
                {{ i18n.table_col_5 }}
              </th>
              <th class="text-left">
                {{ i18n.table_col_6 }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="workspace in workspaces.data"
              :key="workspace.id"
              class="text-left"
            >
              <td class="text-left">
                <Link
                  :href="workspace.path"
                  class="block w-full"
                >
                  <q-avatar
                    size="32px"
                    class="q-mr-sm"
                  >
                    <Jdenticon
                      :text="workspace.id"
                      :size="32"
                    />
                  </q-avatar>
                  {{ workspace.name }}
                </Link>
              </td>
              <td class="text-left">
                <template v-if="workspace.account_manager">
                  <q-avatar size="sm">
                    <img
                      :src="workspace.account_manager.avatar_url"
                      referrerpolicy="no-referrer"
                    >
                    <!-- tooltip with user name -->
                    <q-tooltip :delay="500">
                      <div class="text-bold">
                        {{ workspace.account_manager.name }}
                      </div>
                    </q-tooltip>
                  </q-avatar>
                </template>
                <template v-else>
                  <span class="text-italic">---</span>
                </template>

                <q-btn
                  unelevated
                  color="primary"
                  flat
                  size="sm"
                  round
                  icon="las la-pen"
                  class="q-ml-sm edit-button"
                  @click.stop="dialog_account_manager(workspace)"
                />
              </td>
              <td class="text-left">
                <Link
                  :href="workspace.path"
                  class="block w-full"
                >
                  {{ workspace.accounting_end_date }}
                </Link>
              </td>
              <td class="text-left">
                <Link
                  :href="workspace.path"
                  class="block w-full"
                >
                  <div class="flex">
                    <q-avatar
                      v-for="user in JSON.parse(workspace.users)"
                      :key="user.id"
                      size="sm"
                      class="overlapping-avatars ml-[-5px]"
                      style=""
                    >
                      <img
                        :src="user.avatar_url"
                        referrerpolicy="no-referrer"
                      >
                      <!-- tooltip with user first name last name -->
                      <q-tooltip :delay="500">
                        <div class="text-bold">
                          {{ user.name }}
                        </div>
                      </q-tooltip>
                    </q-avatar>
                  </div>
                </Link>
              </td>
              <td class="text-left">
                <Link
                  :href="workspace.path"
                  class="block w-full"
                >
                  <q-icon
                    :name="workspace.subscription.icon"
                    :color="workspace.subscription.color"
                    class="q-mr-xs"
                  />
                  <span>{{ workspace.subscription.title }}</span>
                  <span class="q-pl-sm text-muted text-caption text-italic">({{ workspace.subscription.label }})
                  </span>
                </Link>
              </td>
              <td class="text-left">
                <label>
                  {{ workspace.accounting_plan.name }}
                  <q-btn
                    unelevated
                    color="primary"
                    flat
                    size="sm"
                    round
                    icon="las la-pen"
                    class="q-ml-sm edit-button"
                    @click.stop="dialog_accounting_plan(workspace)"
                  />
                </label>
              </td>
            </tr>
          </tbody>
        </q-markup-table>

        <!-- Table Pagination -->
        <div
          v-if="workspaces?.meta?.count != 0"
          class="q-table__bottom row items-center justify-end"
        >
          <div class="q-table__control col-12">
            <Pagination
              :limits="[20, 50, 100, 200]"
              :meta="workspaces.meta"
              :prop_keys="['workspaces']"
            />
          </div>
        </div>

        <!-- Display no search results -->
        <q-card
          v-if="workspaces?.meta?.count == 0 && (filter_search || filter_account_manager)"
          class="q-mt-xl"
          flat
        >
          <q-card-section class="text-center">
            <q-icon
              name="las la-search"
              size="lg"
            />
            <h4 class="text-h6 q-mt-md">
              {{ i18n.no_result }}
            </h4>
            <p class="text-caption">
              {{ i18n.no_result_for_search }}
            </p>
          </q-card-section>
        </q-card>

        <!-- Display no clients -->
        <q-card
          v-if="workspaces?.meta?.count == 0 && (!filter_search && !filter_account_manager)"
          class="q-mt-xl"
          flat
        >
          <q-card-section class="text-center">
            <q-icon
              name="las la-users"
              size="lg"
            />
            <h4 class="text-h6 q-mt-md">
              {{ i18n.no_workspace_title }}
            </h4>
            <p class="text-caption">
              {{ i18n.no_workspace_text }}
            </p>
          </q-card-section>
        </q-card>
      </div>
    </div>

    <!-- Edit - Create -->
    <q-dialog v-model="dialog_accounting_plan_show">
      <q-card
        class="q-pa-md"
        style="width: 640px; max-width: 80vw"
      >
        <q-card-section>
          <div class="text-h6">
            {{ i18n.change_accounting_plan }}
          </div>
        </q-card-section>

        <q-card-section class="row q-col-gutter-md">
          <!-- Plan comptable -->
          <div class="col-12">
            <span class="text-input-label">{{
              i18n.accounting_plan
            }}</span>
            <q-select
              v-model="dialog_accounting_plan_select_id"
              outlined
              dense
              class="full-width q-mt-sm"
              :options="$page.props.accounting_plans"
              option-value="id"
              option-label="name"
              :rules="[(val) => val !== null || i18n.required_field]"
              emit-value
              map-options
              fill-input
              use-input
              hide-selected
              input-debounce="0"
            >
              <template v-slot:no-option>
                <q-item>
                  <q-item-section class="text-muted">
                    {{ i18n.no_accounting_plan }}
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
          </div>
          <div class="col-12">
            <q-banner
              class="bg-pastel-green text-dark q-pa-md"
              dense
            >
              <template v-slot:avatar>
                <q-icon
                  name="las la-info-circle"
                  size="sm"
                  color="black"
                />
              </template>
              <div class="">
                {{ i18n.transfer_description }}
                <br>
                {{ i18n.manual_recategroization_note }}
              </div>
            </q-banner>
          </div>
        </q-card-section>

        <q-card-actions
          align="right"
          class="q-mt-md"
        >
          <q-btn
            :label="$q.lang.label.cancel"
            color="secondary"
            flat
            @click="dialog_accounting_plan_show = false"
          />
          <q-btn
            :label="i18n.submit"
            color="primary"
            unelevated
            :loading="dialog_accounting_plan_loading"
            :disable="dialog_accounting_plan_select_id == null"
            @click="dialog_accounting_plan_submit()"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Edit Account Manager -->
    <q-dialog v-model="dialog_account_manager_show">
      <q-card
        class="q-pa-md"
        style="width: 640px; max-width: 80vw"
      >
        <q-card-section>
          <div class="text-h6">
            {{ i18n.dialog_account_manager_title }}
          </div>
        </q-card-section>

        <q-card-section class="row q-col-gutter-md">
          <!-- Account Manager Edit -->
          <div class="col-12">
            <span class="text-input-label">{{
              i18n.dialog_account_manager_select_label
            }}</span>
            <q-select
              v-model="dialog_account_manager_selected"
              outlined
              dense
              class="full-width q-mt-sm"
              :options="account_managers"
              option-label="name"
              emit-value
              map-options
              hide-bottom-space
              :clearable="!dialog_account_manager_current"
            >
              <template v-slot:option="scope">
                <q-item v-bind="scope.itemProps">
                  <q-item-section avatar>
                    <q-avatar size="md">
                      <img
                        :src="scope.opt.avatar_url"
                        referrerpolicy="no-referrer"
                      >
                    </q-avatar>
                  </q-item-section>
                  <q-item-section>
                    <q-item-label>{{ scope.opt.name }}</q-item-label>
                    <q-item-label caption>
                      {{ scope.opt.email }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </template>

              <template v-slot:selected>
                <template v-if="dialog_account_manager_selected">
                  <q-avatar
                    size="24px"
                    class="q-mr-sm"
                  >
                    <img
                      :src="dialog_account_manager_selected.avatar_url"
                      referrerpolicy="no-referrer"
                    >
                  </q-avatar>
                  {{ dialog_account_manager_selected.name }}
                </template>
                <template v-else>
                  {{ i18n.dialog_account_manager_select_placeholder }}
                </template>
              </template>

              <template v-slot:no-option>
                <q-item>
                  <q-item-section class="text-muted">
                    {{ 't(workspaceGroups.clients.noUsers)' }}
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
          </div>

          <!-- Remove manager button -->
          <div
            v-if="dialog_account_manager_current"
            class="col-12 q-gutter-sm"
          >
            <q-btn
              flat
              color="negative"
              icon="las la-user-slash"
              :label="i18n.dialog_account_manager_btn_remove"
              :disable="!dialog_account_manager_selected"
              @click="dialog_account_manager_remove"
            />
          </div>
        </q-card-section>

        <q-card-actions
          align="right"
          class="q-mt-md"
        >
          <q-btn
            :label="$q.lang.label.cancel"
            color="secondary"
            flat
            @click="dialog_account_manager_show = false"
          />
          <q-btn
            :label="$q.lang.label.update"
            color="primary"
            unelevated
            :disable="dialog_account_manager_selected == dialog_account_manager_current"
            :loading="dialog_account_manager_loading"
            @click="dialog_account_manager_submit"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import Layout from '@/layouts/wg_admin.vue';
import { Link } from '@inertiajs/vue3';
import Jdenticon from '@/shared/jdenticon.vue';
import Pagination from '@/shared/pagination.vue';
import { VOnboardingWrapper, VOnboardingStep } from 'v-onboarding';

export default {
  components: {
    Jdenticon,
    Link,
    Pagination,
    VOnboardingStep,
    VOnboardingWrapper,
  },
  layout: Layout,
  props: {
    i18n: {
      type: Object,
      required: true,
    },
    vonboarding: {
      type: [Object, null],
      required: true,
    },
    filters: {
      type: Object,
      required: false,
    },
    account_managers: {
      type: Array,
      required: true,
    },
    workspaces: {
      type: Object,
      required: true,
    },
    workspaces_new_link: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      filter_search: this.filters?.search,
      filter_account_manager: this.account_managers.find((am) => am.id === this.filters.account_manager),
      dialog_account_manager_loading: false,
      dialog_account_manager_action_path: null,
      dialog_account_manager_current: null,
      dialog_account_manager_select_workspace_id: null,
      dialog_account_manager_selected: null,
      dialog_account_manager_show: false,
      dialog_accounting_plan_action_path: null,
      dialog_accounting_plan_loading: false,
      dialog_accounting_plan_select_id: null,
      dialog_accounting_plan_select_workspace_id: null,
      dialog_accounting_plan_show: false,
    };
  },
  computed: {
  },
  watch: {
    filter_search(new_value) {
      this.$inertia.get(
        this.$page.props.workspaces.search_path,
        {
          search: new_value,
          account_manager: this.filter_account_manager?.id,
        },
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: ['workspaces'],
        },
      );
    },
    filter_account_manager(new_value) {
      this.$inertia.get(
        this.$page.props.workspaces.search_path,
        {
          search: this.filter_search,
          account_manager: new_value?.id,
        },
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: ['workspaces'],
        },
      );
    }
  },
  mounted() {
    if (this.vonboarding) {
      setTimeout(() => {
        this.$refs.vo_wrapper.start();
      }, 1000);
    }
  },
  methods: {
    vonboarding_finish() {
      this.$inertia.post(
        this.vonboarding.update_path,
        { key: this.vonboarding.announcement_key },
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: ['vonboarding'],
        },
      );
      this.$refs.vo_wrapper.finish();
    },
    dialog_account_manager(workspace) {
      this.dialog_account_manager_selected = workspace.account_manager;
      this.dialog_account_manager_current = workspace.account_manager;
      this.dialog_account_manager_select_workspace_id = workspace.id;
      this.dialog_account_manager_action_path = workspace.update_path;
      this.dialog_account_manager_show = true;
    },
    dialog_account_manager_submit() {
      this.$inertia.put(
        this.dialog_account_manager_action_path,
        {
          workspace_id: this.dialog_account_manager_select_workspace_id,
          account_manager_id: (this.dialog_account_manager_selected?.id ?? null),
        },
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
            this.dialog_account_manager_loading = true;
          },
          onSuccess: () => {
            this.dialog_account_manager_show = false;
            this.$q.notify({
              message: this.i18n.save_success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.i18n.save_error,
              type: "negative",
            });
          },
          onFinish: () => {
            this.dialog_account_manager_loading = false;
          }
        }
      );
    },
    dialog_account_manager_remove() {
      this.dialog_account_manager_selected = null;
      this.dialog_account_manager_submit();
    },
    dialog_accounting_plan(workspace) {
      this.dialog_accounting_plan_select_id = workspace.accounting_plan.id;
      this.dialog_accounting_plan_select_workspace_id = workspace.id;
      this.dialog_accounting_plan_action_path = workspace.update_path;
      this.dialog_accounting_plan_show = true;
    },
    dialog_accounting_plan_submit() {
      this.$inertia.put(
        this.dialog_accounting_plan_action_path,
        {
          workspace_id: this.dialog_accounting_plan_select_workspace_id,
          accounting_plan_id: this.dialog_accounting_plan_select_id,
        },
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
            this.dialog_accounting_plan_loading = true;
          },
          onSuccess: () => {
            this.dialog_accounting_plan_show = false;
            this.$q.notify({
              message: this.i18n.save_success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.i18n.save_error,
              type: "negative",
            });
          },
          onFinish: () => {
            this.dialog_accounting_plan_loading = false;
          }
        }
      );
    },
  },
};
</script>
<style scoped>
/* Hide the edit button until hovered into */
.edit-button {
  opacity: 0;
  transition: opacity 0.1s;
}

/* Using deep selector to target the table row hover state */
:deep(.q-table tbody tr:hover) .edit-button {
  opacity: 1;
}
</style>
